/**
 * Upload the supporting documents for one work-order action (reject,
 * request-more-info, reply) and report anything that could not be uploaded.
 *
 * CWL-115 (R001-F002, R001-F007). This exists so the three action call sites do
 * not each hand-roll the same decision. Two invariants live here, not there:
 *
 *  - It ALWAYS resolves with an attachment array. A total failure yields `[]`,
 *    never a throw — the action must still be submitted with zero attachments
 *    (plan CWL-115 Decision D3). There is deliberately no early return for the
 *    caller to get wrong.
 *  - Partial success is a WARNING, not an error. The action succeeds, so the
 *    mutation's own success toast fires; a red toast alongside it told the user
 *    two contradictory things about one action.
 *
 * `failures` is returned as well so a batched caller (JobList's bulk reject) can
 * accumulate across jobs and warn once — pass `notify: false` and use
 * `describeJobUploadFailures` to build that single, job-named warning.
 */

import { describeUploadFailures } from '@/domains/jobs/lib/describeUploadFailures';
import {
  type JobDocumentUploadResult,
  uploadJobDocuments,
} from '@/domains/jobs/lib/uploadJobDocuments';
import { showWarningToast } from '@/shared/notifications/toast-utils';

export interface UploadActionAttachmentsOptions {
  /**
   * Show the per-action warning toast. Default true. A batched caller (JobList's
   * bulk reject) passes false and warns once for the whole batch with each job
   * named, instead of one toast per job (R001-F015).
   */
  notify?: boolean;
}

export async function uploadActionAttachments(
  jobId: number,
  files: File[] | undefined,
  options?: UploadActionAttachmentsOptions,
): Promise<JobDocumentUploadResult> {
  if (!files?.length) return { attachments: [], failures: [] };

  const result = await uploadJobDocuments(jobId, files);

  if (options?.notify !== false) {
    const failureMessage = describeUploadFailures(result.failures);
    if (failureMessage) {
      showWarningToast('Some attachments were not uploaded', failureMessage);
    }
  }

  return result;
}
